import { createComponent } from '@carvana/merch-components';
import useAnchorClickMessageDispatcher from '@carvana/merch-native/useAnchorClickMessageDispatcher';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { isNonProd } from '~/constants/environment';
import { logError } from '~/utilities/loggers';

const PageHeader = dynamic(
  () => import(/* webpackChunkName: "PageHeader" */ '@/common/PageHeader'),
  { ssr: false }
);

const ErrorSadCar = `${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/error-sadcar-device.png`;

const ErrorBoundaryFallbackWrapper = createComponent('div', {
  className: 'min-h-[50vh] text-center'
});

const ErrorBoundaryFallbackTitle = createComponent('p', {
  className: 'mt-48 tl-header-xxl'
});

const ErrorBoundaryFallbackLogo = createComponent('img', {
  className: 'mx-auto my-48 h-[118px] w-[118px]'
});

const ErrorBoundaryFallbackLink = createComponent('a', {
  className: 'font-bold text-blue-2 no-underline'
});

export type OwnProps = {
  componentStack?: string;
  error?: Error;
  statusCode?: number;
};

export default function ErrorBoundaryFallbackComponent({
  componentStack,
  error,
  statusCode
}: OwnProps) {
  const errorCopy =
    statusCode === 404
      ? 'We could not find that vehicle.'
      : 'It appears there may have been a problem.';

  useAnchorClickMessageDispatcher({
    logError,
    source: 'error-page',
    verboseLogs: isNonProd
  });

  return (
    <>
      <Head>
        <title>Carvana | Buy Used Cars Online & Skip The Dealership</title>
      </Head>

      <PageHeader />

      <ErrorBoundaryFallbackWrapper>
        <ErrorBoundaryFallbackTitle>
          Oops! {statusCode}
        </ErrorBoundaryFallbackTitle>
        <p className="mt-8">{errorCopy}</p>
        <p>We are sorry for the inconvenience.</p>

        <div>
          <ErrorBoundaryFallbackLogo src={ErrorSadCar} />
        </div>

        {process.env.NODE_ENV === 'production' || statusCode === 404 ? (
          <ErrorBoundaryFallbackLink
            href="/cars"
            data-analytics-id="Error Boundary - Back to Search"
            data-testid="back-to-search-link"
          >
            Get back to SEARCH
          </ErrorBoundaryFallbackLink>
        ) : (
          <div className="whitespace-pre-wrap">
            {error && error.toString()}
            <br />
            {componentStack}
          </div>
        )}
      </ErrorBoundaryFallbackWrapper>
    </>
  );
}
